import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import axios from 'axios';
import { useFolder } from '../contexts/FolderContext';
import { stripLatestWeekAggregatedActuals, stripLatestWeekActuals } from '../utils/collatedLatestWeek';
import { filterOptumSkus } from '../utils/optumSkuFilter';
import { filterAllowedItems } from '../utils/allowedItemsFilter';

// Holiday model dates to exclude: model_date falls on these (month, day)
const HOLIDAY_MODEL_DATES = [
  { month: 8, day: 27 },   // 27 Sep (model_date 20250927)
  { month: 9, day: 18 },   // 18 Oct
  { month: 11, day: 20 },  // 20 Dec
  { month: 11, day: 27 },  // 27 Dec
  { month: 0, day: 10 },   // 10 Jan
];

function isModelDateHolidayWeek(modelDateStr) {
  if (modelDateStr == null || modelDateStr === '') return false;
  const s = String(modelDateStr).trim().replace(/-/g, '');
  if (s.length !== 8 || !/^\d{8}$/.test(s)) return false;
  const m = parseInt(s.slice(4, 6), 10) - 1;
  const d = parseInt(s.slice(6, 8), 10);
  return HOLIDAY_MODEL_DATES.some(h => m === h.month && d === h.day);
}

function AccuracyTrends({ fileName = 'collated_comparison_aggregated' }) {
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [data, setData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'foodcourt_name', direction: 'asc' });
  const [fcMapping, setFcMapping] = useState({});
  const [restaurantMapping, setRestaurantMapping] = useState({});
  const { selectedRunDate, foldersByRunDate } = useFolder();

  const latestModelDate = useMemo(() => {
    if (!selectedRunDate) return '';
    const folders = foldersByRunDate[selectedRunDate] || [];
    if (folders.length === 0) return '';
    return folders[folders.length - 1]?.modelDate || '';
  }, [selectedRunDate, foldersByRunDate]);

  // Helper function to convert to proper case
  const toProperCase = (str) => {
    if (!str) return str;
    return str
      .split(/[\s_]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Load FC and restaurant mappings
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const [fcrResponse, fcMappingRes, restaurantsRes] = await Promise.all([
          axios.get('/api/fcr-mapping').catch(() => ({ data: [] })),
          axios.get('/api/fc-mapping').catch(() => ({ data: [] })),
          axios.get('/api/restaurants').catch(() => ({ data: [] }))
        ]);
        
        const fcMap = {};
        const restMap = {};
        
        // Load FC mapping from fc_mapping.csv (priority 1)
        if (fcMappingRes.data && fcMappingRes.data.length > 0) {
          fcMappingRes.data.forEach(row => {
            const fcId = (row.foodcourtid || '').toString().trim();
            const fcName = (row.foodcourtname || '').toString().trim();
            if (fcId && fcName) {
              fcMap[fcId] = fcName;
            }
          });
        }
        
        // Load from FCR mapping (priority 2 - for FC names not in fc_mapping.csv)
        if (fcrResponse.data && fcrResponse.data.length > 0) {
          fcrResponse.data.forEach(row => {
            const fcId = (row.foodcourtid || row.foodcourt_id || '').toString().trim();
            const fcName = (row.foodcourtname || row.foodcourt_name || '').toString().trim();
            const restId = (row.restaurant || row.restaurant_id || '').toString().trim();
            const restName = (row.restaurantname || row.restaurant_name || '').toString().trim();
            
            if (fcId && fcName && !fcMap[fcId]) {
              fcMap[fcId] = fcName;
            }
            if (restId && restName) {
              restMap[restId] = restName;
            }
          });
        }
        
        // Load restaurant mapping from /api/restaurants
        if (restaurantsRes.data && restaurantsRes.data.length > 0) {
          restaurantsRes.data.forEach(rest => {
            const restId = (rest.id || '').toString().trim();
            const restName = (rest.name || '').toString().trim();
            if (restId && restName && !restMap[restId]) {
              restMap[restId] = restName;
            }
          });
        }
        
        setFcMapping(fcMap);
        setRestaurantMapping(restMap);
      } catch (err) {
        console.error('Error loading mappings:', err);
      }
    };
    
    loadMappings();
  }, []);

  // Fetch collated data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedRunDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [aggResult, comparisonResult] = await Promise.allSettled([
          axios.get(`/api/collated-data/${selectedRunDate}`, {
            params: { file: fileName }
          }),
          axios.get(`/api/collated-data/${selectedRunDate}`, {
            params: { file: 'collated_comparison' }
          })
        ]);

        if (aggResult.status === 'fulfilled') {
          setData(aggResult.value.data || []);
        } else {
          throw aggResult.reason;
        }

        if (comparisonResult.status === 'fulfilled') {
          setComparisonData(comparisonResult.value.data || []);
        } else {
          setComparisonData([]);
        }
      } catch (err) {
        console.error('Error fetching collated data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedRunDate, fileName]);

  // Helper functions
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      let date;
      // Check if dateStr is in YYYYMMDD format (8 digits)
      if (typeof dateStr === 'string' && dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
        // Parse YYYYMMDD format
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        // Try standard date parsing
        date = new Date(dateStr);
      }
      
      if (isNaN(date.getTime())) {
        // Invalid date
        return dateStr;
      }
      const dayNum = date.getDate();
      const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
      const yearNum = date.getFullYear();
      if (isNaN(dayNum)) {
        return dateStr;
      }
      return `${dayNum} ${monthStr} ${yearNum}`;
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return num.toFixed(1);
  };

  const toNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const pickMapeValue = (row) => {
    const candidates = [
      row.APE_component_model,
      row.APE_component,
      row.APE_frid,
      row.APE_old,
      row.APE_friw,
      row.APE,
      row.ape,
      row.MAPE,
      row.mape
    ];
    for (const candidate of candidates) {
      const value = toNumber(candidate);
      if (Number.isFinite(value)) {
        return value;
      }
    }
    return null;
  };

  const pickMadValue = (row) => {
    const candidates = [
      row.MAD_component_model,
      row.MAD_component,
      row.MAD_frid,
      row.MAD_old,
      row.MAD_friw,
      row.MAD,
      row.mad
    ];
    for (const candidate of candidates) {
      const value = toNumber(candidate);
      if (Number.isFinite(value)) {
        return value;
      }
    }
    return null;
  };

  const pickActualValue = (row) => {
    const candidates = [
      row.actual_qty,
      row.actual,
      row.actual_qty_component_model,
      row.actual_qty_component,
      row.actual_qty_frid,
      row.actual_qty_old,
      row.actual_qty_friw
    ];
    for (const candidate of candidates) {
      const value = toNumber(candidate);
      if (Number.isFinite(value)) {
        return value;
      }
    }
    return null;
  };

  const calculateStatusFromMetrics = (mape, mad) => {
    if (mape === null || mad === null || isNaN(mape) || isNaN(mad)) {
      return '';
    }
    
    const accuracyPct = (1 - mape) * 100; // MAPE is a decimal (0-1), convert to accuracy percentage
    
    if (accuracyPct > 90) {
      return 'Green';
    } else if (accuracyPct >= 70) {
      if (mad <= 5) {
        return 'Green';
      } else if (mad <= 15) {
        return 'Orange';
      } else {
        return 'Orange';
      }
    } else { // accuracy < 70%
      if (mad <= 5) {
        return 'Green';
      } else if (mad <= 15) {
        return 'Orange';
      } else {
        return 'Red';
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Green': return '#c8e6c9';
      case 'Orange': return '#ffe0b2';
      case 'Red': return '#ffcdd2';
      default: return 'grey.50';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'Green': return '#2e7d32';
      case 'Orange': return '#e65100';
      case 'Red': return '#c62828';
      default: return 'inherit';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredData = useMemo(() => {
    let rows = filterAllowedItems(filterOptumSkus(data || []));
    if (excludeHolidays && rows.length > 0) {
      rows = rows.filter(row => !isModelDateHolidayWeek(row.model_date));
    }
    if (latestModelDate && rows.length > 0) {
      rows = rows.map(row => stripLatestWeekAggregatedActuals(row, latestModelDate, selectedRunDate));
    }
    return rows;
  }, [data, excludeHolidays, latestModelDate, selectedRunDate]);

  const filteredComparisonData = useMemo(() => {
    let rows = filterAllowedItems(filterOptumSkus(comparisonData || []));
    if (excludeHolidays && rows.length > 0) {
      rows = rows.filter(row => !isModelDateHolidayWeek(row.model_date));
    }
    if (latestModelDate && rows.length > 0) {
      rows = rows.map(row => stripLatestWeekActuals(row, latestModelDate, selectedRunDate));
    }
    return rows;
  }, [comparisonData, excludeHolidays, latestModelDate, selectedRunDate]);

  const comparisonMetrics = useMemo(() => {
    if (!filteredComparisonData || filteredComparisonData.length === 0) {
      return { byKey: new Map(), overall: { mape: null, mad: null }, hasData: false };
    }

    const byKey = new Map();
    let totalMapeWeighted = 0;
    let totalMadWeighted = 0;
    let totalWeight = 0;

    filteredComparisonData.forEach((row) => {
      const foodcourtid = (row.foodcourtid || '').toString().trim();
      const restaurant = (row.restaurant || '').toString().trim();
      const itemname = (row.itemname || '').toString().trim();
      if (!foodcourtid || !restaurant || !itemname) {
        return;
      }

      const actual = pickActualValue(row);
      if (!Number.isFinite(actual) || actual <= 0) {
        return;
      }

      const mape = pickMapeValue(row);
      const mad = pickMadValue(row);
      const key = `${foodcourtid}||${restaurant}||${itemname}`;
      const entry = byKey.get(key) || { mapeWeighted: 0, madWeighted: 0, weight: 0 };

      if (Number.isFinite(mape)) {
        entry.mapeWeighted += mape * actual;
        totalMapeWeighted += mape * actual;
      }
      if (Number.isFinite(mad)) {
        entry.madWeighted += mad * actual;
        totalMadWeighted += mad * actual;
      }
      entry.weight += actual;
      totalWeight += actual;

      byKey.set(key, entry);
    });

    const overallMape = totalWeight > 0 ? totalMapeWeighted / totalWeight : null;
    const overallMad = totalWeight > 0 ? totalMadWeighted / totalWeight : null;

    return { byKey, overall: { mape: overallMape, mad: overallMad }, hasData: totalWeight > 0 };
  }, [filteredComparisonData]);

  const comparisonOverall = useMemo(() => {
    if (!comparisonMetrics.hasData) {
      return { accuracy: null, mad: null, status: '' };
    }
    const accuracy = comparisonMetrics.overall.mape !== null
      ? (1 - comparisonMetrics.overall.mape) * 100
      : null;
    const mad = comparisonMetrics.overall.mad;
    const status = calculateStatusFromMetrics(comparisonMetrics.overall.mape, comparisonMetrics.overall.mad);
    return { accuracy, mad, status };
  }, [comparisonMetrics]);

  // Transform data into pivot table format
  const transformedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { rows: [], dates: [], overallMetrics: {} };
    }

    // Include items that have at least 1 day scored in at least one of the last 4 model dates (show all FRIs with any data)
    const allDatesSorted = [...new Set(filteredData.map(r => r.model_date).filter(Boolean))].sort();
    const last4Dates = new Set(allDatesSorted.slice(-4));
    const validFriKeys = new Set();
    filteredData.forEach((row) => {
      const modelDate = row.model_date;
      if (!modelDate || !last4Dates.has(modelDate)) return;
      const numDays = parseFloat(row.num_days || 0) || 0;
      if (numDays < 1) return;
      const key = `${row.foodcourtid}_${row.restaurant}_${row.itemname}`;
      validFriKeys.add(key);
    });

    // Group by foodcourtid, restaurant, itemname
    const grouped = {};
    const allDates = new Set();

    filteredData.forEach(row => {
      const key = `${row.foodcourtid}_${row.restaurant}_${row.itemname}`;
      const modelDate = row.model_date;
      
      if (modelDate) {
        allDates.add(modelDate);
      }

      if (!grouped[key]) {
        grouped[key] = {
          foodcourtid: row.foodcourtid,
          foodcourt_name: fcMapping[row.foodcourtid] || row.foodcourtid,
          restaurant: row.restaurant,
          restaurant_name: restaurantMapping[row.restaurant] || row.restaurant,
          itemname: row.itemname,
          dates: {}
        };
      }

      if (modelDate) {
        grouped[key].dates[modelDate] = {
          accuracy: row.avg_mape !== null && row.avg_mape !== undefined 
            ? (1 - parseFloat(row.avg_mape)) * 100 
            : null,
          abs_error: row.avg_mad !== null && row.avg_mad !== undefined 
            ? parseFloat(row.avg_mad) 
            : null,
          num_days: row.num_days,
          avg_daily_units_sold: row.avg_daily_units_sold,
          avg_mape: row.avg_mape,
          avg_mad: row.avg_mad
        };
      }
    });

    // Calculate overall metrics per date (only for valid FRIs)
    const overallMetrics = {};
    Array.from(allDates).sort().forEach(date => {
      const dateRows = filteredData.filter(
          (row) =>
            row.model_date === date &&
          validFriKeys.has(`${row.foodcourtid}_${row.restaurant}_${row.itemname}`)
      );
      let totalMAPEWeighted = 0;
      let totalMADWeighted = 0;
      let totalUnitsForMAPE = 0;
      let totalUnitsForMAD = 0;
      
      dateRows.forEach(row => {
        const mape = parseFloat(row.avg_mape);
        const mad = parseFloat(row.avg_mad);
        const numDays = parseFloat(row.num_days || 0) || 0;
        const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
        const totalUnitsSold = numDays * avgDailyUnits;
        
        if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
          totalMAPEWeighted += mape * totalUnitsSold;
          totalUnitsForMAPE += totalUnitsSold;
        }
        
        if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
          totalMADWeighted += mad * totalUnitsSold;
          totalUnitsForMAD += totalUnitsSold;
        }
      });
      
      const avgMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
      const avgMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
      const overallAccuracy = avgMAPE !== null ? (1 - avgMAPE) * 100 : null;
      
      overallMetrics[date] = {
        accuracy: overallAccuracy,
        abs_error: avgMAD
      };
    });

    // Transform to array format, keeping only items with at least 1 day scored in one of last 4 model dates
    const result = Object.values(grouped)
      .filter((item) => {
        const key = `${item.foodcourtid}_${item.restaurant}_${item.itemname}`;
        return validFriKeys.has(key);
      })
      .map(item => {
      const row = {
        foodcourtid: item.foodcourtid,
        foodcourt_name: item.foodcourt_name,
        restaurant: item.restaurant,
        restaurant_name: item.restaurant_name,
        itemname: item.itemname
      };

      const sortedDates = Array.from(allDates).sort();
      
      sortedDates.forEach(date => {
        const dateData = item.dates[date];
        if (dateData) {
          row[`${date}_accuracy`] = dateData.accuracy;
          row[`${date}_abs_error`] = dateData.abs_error;
          row[`${date}_num_days`] = dateData.num_days;
          row[`${date}_avg_daily_units_sold`] = dateData.avg_daily_units_sold;
        } else {
          row[`${date}_accuracy`] = null;
          row[`${date}_abs_error`] = null;
          row[`${date}_num_days`] = 0;
          row[`${date}_avg_daily_units_sold`] = 0;
        }
      });

      // Calculate overall weighted average across all dates for this item
      let totalMAPEWeighted = 0;
      let totalMADWeighted = 0;
      let totalUnitsForMAPE = 0;
      let totalUnitsForMAD = 0;
      
      sortedDates.forEach(date => {
        const dateData = item.dates[date];
        if (dateData) {
          const numDays = dateData.num_days || 0;
          const avgDailyUnits = dateData.avg_daily_units_sold || 0;
          const totalUnitsSold = numDays * avgDailyUnits;
          const mape = dateData.avg_mape || 0;
          const mad = dateData.avg_mad || 0;
          
          if (mape >= 0 && totalUnitsSold > 0) {
            totalMAPEWeighted += mape * totalUnitsSold;
            totalUnitsForMAPE += totalUnitsSold;
          }
          
          if (mad >= 0 && totalUnitsSold > 0) {
            totalMADWeighted += mad * totalUnitsSold;
            totalUnitsForMAD += totalUnitsSold;
          }
        }
      });
      
      const overallMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
      const overallMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
      row.overall_accuracy = overallMAPE !== null ? (1 - overallMAPE) * 100 : null;
      row.overall_abs_error = overallMAD;
      row.overall_status = calculateStatusFromMetrics(overallMAPE, overallMAD);

      if (comparisonMetrics.hasData) {
        const comparisonKey = `${row.foodcourtid}||${row.restaurant}||${row.itemname}`;
        const comparisonEntry = comparisonMetrics.byKey.get(comparisonKey);
        if (comparisonEntry && comparisonEntry.weight > 0) {
          const compMape = comparisonEntry.mapeWeighted / comparisonEntry.weight;
          const compMad = comparisonEntry.madWeighted / comparisonEntry.weight;
          row.overall_accuracy = compMape !== null ? (1 - compMape) * 100 : null;
          row.overall_abs_error = compMad;
          row.overall_status = calculateStatusFromMetrics(compMape, compMad);
        }
      }

      return row;
    });

    return { rows: result, dates: Array.from(allDates).sort(), overallMetrics };
  }, [filteredData, fcMapping, restaurantMapping, comparisonMetrics]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) {
      return transformedData.rows;
    }

    const sorted = [...transformedData.rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [transformedData.rows, sortConfig]);

  // Calculate column widths (80% of original widths)
  const fcWidth = 200; // 80% of 250
  const restaurantWidth = 165; // 80% of 200 + 5px
  const itemNameWidth = 160; // 80% of 200

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3 }}>
      <Card elevation={1} sx={{ mb: 4, maxWidth: '95%', width: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeHolidays}
                onChange={(e) => setExcludeHolidays(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                Exclude Holiday Periods
              </Typography>
            }
          />
        </Box>
        <TableContainer sx={{ overflowX: 'auto', overflowY: 'auto', border: '1px solid', borderColor: 'divider', maxHeight: '80vh' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { padding: '4px 6px', boxSizing: 'border-box' },
              whiteSpace: 'nowrap'
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell 
                  rowSpan={2}
                  align="left"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: fcWidth, 
                    width: fcWidth,
                    maxWidth: fcWidth,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    position: 'sticky',
                    left: 0,
                    zIndex: 20,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                    '&:hover': { bgcolor: 'grey.200' }
                  }} 
                  onClick={() => handleSort('foodcourt_name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    Food Court
                    {sortConfig.key === 'foodcourt_name' ? (
                      sortConfig.direction === 'asc' ? (
                        <ArrowUpward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      )
                    ) : (
                      <Box sx={{ width: '14px', height: '14px' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  rowSpan={2}
                  align="left"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: restaurantWidth, 
                    width: restaurantWidth,
                    maxWidth: restaurantWidth,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    position: 'sticky',
                    left: fcWidth,
                    zIndex: 19,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                    '&:hover': { bgcolor: 'grey.200' }
                  }} 
                  onClick={() => handleSort('restaurant_name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    Restaurant
                    {sortConfig.key === 'restaurant_name' ? (
                      sortConfig.direction === 'asc' ? (
                        <ArrowUpward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      )
                    ) : (
                      <Box sx={{ width: '14px', height: '14px' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  rowSpan={2}
                  align="left"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: itemNameWidth, 
                    width: itemNameWidth,
                    maxWidth: itemNameWidth,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    position: 'sticky',
                    left: fcWidth + restaurantWidth,
                    zIndex: 18,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                    '&:hover': { bgcolor: 'grey.200' }
                  }} 
                  onClick={() => handleSort('itemname')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    Item Name
                    {sortConfig.key === 'itemname' ? (
                      sortConfig.direction === 'asc' ? (
                        <ArrowUpward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: '14px', color: 'primary.main' }} />
                      )
                    ) : (
                      <Box sx={{ width: '14px', height: '14px' }} />
                    )}
                  </Box>
                </TableCell>
                {transformedData.dates.map((date, dateIdx) => (
                  <TableCell
                    key={date}
                    colSpan={2}
                    align="center"
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.7rem', 
                      bgcolor: dateIdx % 2 === 0 ? 'grey.100' : 'grey.200',
                      borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '2px solid',
                      borderColor: 'divider',
                      width: 160,
                      minWidth: 160,
                      position: 'sticky',
                      top: 0,
                      zIndex: 12,
                      boxShadow: '0 2px 4px -2px rgba(0,0,0,0.1)'
                    }}
                  >
                    {formatDate(date)}
                  </TableCell>
                ))}
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    bgcolor: 'grey.100',
                    width: 240,
                    minWidth: 240,
                    position: 'sticky',
                    top: 0,
                    zIndex: 12,
                    boxShadow: '0 2px 4px -2px rgba(0,0,0,0.1)'
                  }}
                >
                  All weeks (Weighted avg)
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  '& .MuiTableCell-root': {
                    position: 'sticky',
                    top: 40,
                    zIndex: 11
                  }
                }}
              >
                {transformedData.dates.map((date, dateIdx) => (
                  <React.Fragment key={date}>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.7rem', 
                        bgcolor: dateIdx % 2 === 0 ? 'grey.100' : 'grey.200',
                        borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        width: 80,
                        minWidth: 80
                      }}
                    >
                      Accuracy%
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.7rem', 
                        bgcolor: dateIdx % 2 === 0 ? 'grey.100' : 'grey.200',
                        borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '2px solid',
                        borderColor: 'divider',
                        width: 80,
                        minWidth: 80
                      }}
                    >
                      Abs. Error
                    </TableCell>
                  </React.Fragment>
                ))}
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    bgcolor: 'grey.100',
                    width: 80,
                    minWidth: 80
                  }}
                >
                  Accuracy%
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    bgcolor: 'grey.100',
                    width: 80,
                    minWidth: 80
                  }}
                >
                  Abs. Error
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    bgcolor: 'grey.100',
                    width: 80,
                    minWidth: 80
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3 + transformedData.dates.length * 2 + 3} align="center" sx={{ fontSize: '0.65rem' }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row, idx) => {
                  const rowBg = idx % 2 === 0 ? 'background.paper' : 'grey.50';
                  return (
                    <TableRow key={idx} sx={{ bgcolor: rowBg }}>
                      <TableCell 
                        align="left" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          borderRight: '2px solid', 
                          borderColor: 'divider', 
                          minWidth: fcWidth, 
                          width: fcWidth,
                          maxWidth: fcWidth,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.2,
                          position: 'sticky',
                          left: 0,
                          zIndex: 15,
                          bgcolor: rowBg,
                          boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                          paddingLeft: '12px',
                          paddingRight: '8px'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.65rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {row.foodcourt_name && row.foodcourt_name !== row.foodcourtid ? row.foodcourt_name : row.foodcourtid}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          borderRight: '2px solid', 
                          borderColor: 'divider', 
                          minWidth: restaurantWidth, 
                          width: restaurantWidth,
                          maxWidth: restaurantWidth,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.2,
                          position: 'sticky',
                          left: fcWidth,
                          zIndex: 14,
                          bgcolor: rowBg,
                          boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                          paddingLeft: '12px',
                          paddingRight: '8px'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.65rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {row.restaurant_name && row.restaurant_name !== row.restaurant ? row.restaurant_name : row.restaurant}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          borderRight: '2px solid', 
                          borderColor: 'divider', 
                          minWidth: itemNameWidth, 
                          width: itemNameWidth,
                          maxWidth: itemNameWidth,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.2,
                          position: 'sticky',
                          left: fcWidth + restaurantWidth,
                          zIndex: 13,
                          bgcolor: rowBg,
                          boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                          paddingLeft: '12px',
                          paddingRight: '8px'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.65rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {toProperCase(row.itemname)}
                        </Typography>
                      </TableCell>
                      {transformedData.dates.map((date, dateIdx) => {
                        const dateBg = dateIdx % 2 === 0 ? 'grey.100' : 'grey.200';
                        return (
                          <React.Fragment key={date}>
                            <TableCell 
                              align="center" 
                              sx={{ 
                                fontSize: '0.65rem', 
                                bgcolor: dateBg,
                                borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '1px solid',
                                borderColor: 'divider',
                                width: 80,
                                minWidth: 80
                              }}
                            >
                              {formatNumber(row[`${date}_accuracy`])}
                              {row[`${date}_accuracy`] !== null && row[`${date}_accuracy`] !== undefined && '%'}
                            </TableCell>
                            <TableCell 
                              align="center" 
                              sx={{ 
                                fontSize: '0.65rem', 
                                bgcolor: dateBg,
                                borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '2px solid',
                                borderColor: 'divider',
                                width: 80,
                                minWidth: 80
                              }}
                            >
                              {formatNumber(row[`${date}_abs_error`])}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      {/* Overall column for item */}
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: 'grey.50',
                          width: 80,
                          minWidth: 80
                        }}
                      >
                        {row.overall_accuracy !== null && row.overall_accuracy !== undefined 
                          ? `${row.overall_accuracy.toFixed(1)}%` 
                          : '-'}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: 'grey.50',
                          width: 80,
                          minWidth: 80
                        }}
                      >
                        {row.overall_abs_error !== null && row.overall_abs_error !== undefined 
                          ? row.overall_abs_error.toFixed(1) 
                          : '-'}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: row.overall_status ? getStatusColor(row.overall_status) : 'grey.50',
                          color: row.overall_status ? getStatusTextColor(row.overall_status) : 'inherit',
                          width: 80,
                          minWidth: 80
                        }}
                      >
                        {row.overall_status || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {/* Overall Summary Row */}
              <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                <TableCell 
                  align="left" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.65rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: fcWidth, 
                    width: fcWidth,
                    maxWidth: fcWidth,
                    position: 'sticky',
                    left: 0,
                    zIndex: 15,
                    bgcolor: 'grey.50',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px'
                  }}
                >
                  Overall
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.65rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: restaurantWidth, 
                    width: restaurantWidth,
                    maxWidth: restaurantWidth,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    position: 'sticky',
                    left: fcWidth,
                    zIndex: 14,
                    bgcolor: 'grey.50',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px'
                  }}
                >
                  -
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.65rem', 
                    borderRight: '2px solid', 
                    borderColor: 'divider', 
                    minWidth: itemNameWidth, 
                    width: itemNameWidth,
                    maxWidth: itemNameWidth,
                    position: 'sticky',
                    left: fcWidth + restaurantWidth,
                    zIndex: 13,
                    bgcolor: 'grey.50',
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)',
                    paddingLeft: '12px',
                    paddingRight: '8px'
                  }}
                >
                  -
                </TableCell>
                {transformedData.dates.map((date, dateIdx) => {
                  const dateBg = dateIdx % 2 === 0 ? 'grey.100' : 'grey.200';
                  const metrics = transformedData.overallMetrics[date];
                  return (
                    <React.Fragment key={date}>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: dateBg,
                          borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '1px solid',
                          borderColor: 'divider',
                          width: 80,
                          minWidth: 80
                        }}
                      >
                        {metrics && metrics.accuracy !== null && metrics.accuracy !== undefined 
                          ? `${metrics.accuracy.toFixed(1)}%` 
                          : '-'}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: dateBg,
                          borderRight: dateIdx === transformedData.dates.length - 1 ? 'none' : '2px solid',
                          borderColor: 'divider',
                          width: 80,
                          minWidth: 80
                        }}
                      >
                        {metrics && metrics.abs_error !== null && metrics.abs_error !== undefined 
                          ? metrics.abs_error.toFixed(1) 
                          : '-'}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                {/* Overall average across all dates */}
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 600,
                    bgcolor: 'grey.50',
                    width: 80,
                    minWidth: 80
                  }}
                >
                  {comparisonMetrics.hasData
                    ? (comparisonOverall.accuracy !== null ? `${comparisonOverall.accuracy.toFixed(1)}%` : '-')
                    : (transformedData.overallMetrics && Object.keys(transformedData.overallMetrics).length > 0 ? (
                      (() => {
                        let totalMAPEWeighted = 0;
                        let totalMADWeighted = 0;
                        let totalUnitsForMAPE = 0;
                        let totalUnitsForMAD = 0;
                        
                        transformedData.dates.forEach(date => {
                          const dateRows = filteredData.filter(row => row.model_date === date);
                          dateRows.forEach(row => {
                            const mape = parseFloat(row.avg_mape);
                            const mad = parseFloat(row.avg_mad);
                            const numDays = parseFloat(row.num_days || 0) || 0;
                            const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
                            const totalUnitsSold = numDays * avgDailyUnits;
                            
                            if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
                              totalMAPEWeighted += mape * totalUnitsSold;
                              totalUnitsForMAPE += totalUnitsSold;
                            }
                            
                            if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
                              totalMADWeighted += mad * totalUnitsSold;
                              totalUnitsForMAD += totalUnitsSold;
                            }
                          });
                        });
                        
                        const avgMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
                        const overallAccuracy = avgMAPE !== null ? (1 - avgMAPE) * 100 : null;
                        
                        return overallAccuracy !== null ? `${overallAccuracy.toFixed(1)}%` : '-';
                      })()
                    ) : '-')}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 600,
                    bgcolor: 'grey.50',
                    width: 80,
                    minWidth: 80
                  }}
                >
                  {comparisonMetrics.hasData
                    ? (comparisonOverall.mad !== null ? comparisonOverall.mad.toFixed(1) : '-')
                    : (transformedData.overallMetrics && Object.keys(transformedData.overallMetrics).length > 0 ? (
                      (() => {
                        let totalMAPEWeighted = 0;
                        let totalMADWeighted = 0;
                        let totalUnitsForMAPE = 0;
                        let totalUnitsForMAD = 0;
                        
                        transformedData.dates.forEach(date => {
                          const dateRows = filteredData.filter(row => row.model_date === date);
                          dateRows.forEach(row => {
                            const mape = parseFloat(row.avg_mape);
                            const mad = parseFloat(row.avg_mad);
                            const numDays = parseFloat(row.num_days || 0) || 0;
                            const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
                            const totalUnitsSold = numDays * avgDailyUnits;
                            
                            if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
                              totalMAPEWeighted += mape * totalUnitsSold;
                              totalUnitsForMAPE += totalUnitsSold;
                            }
                            
                            if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
                              totalMADWeighted += mad * totalUnitsSold;
                              totalUnitsForMAD += totalUnitsSold;
                            }
                          });
                        });
                        
                        const avgMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
                        
                        return avgMAD !== null ? avgMAD.toFixed(1) : '-';
                      })()
                    ) : '-')}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 600,
                    bgcolor: comparisonMetrics.hasData
                      ? (comparisonOverall.status ? getStatusColor(comparisonOverall.status) : 'grey.50')
                      : (() => {
                        if (!transformedData.overallMetrics || Object.keys(transformedData.overallMetrics).length === 0) return 'grey.50';
                        let totalMAPEWeighted = 0;
                        let totalMADWeighted = 0;
                        let totalUnitsForMAPE = 0;
                        let totalUnitsForMAD = 0;
                        
                        transformedData.dates.forEach(date => {
                          const dateRows = filteredData.filter(row => row.model_date === date);
                          dateRows.forEach(row => {
                            const mape = parseFloat(row.avg_mape);
                            const mad = parseFloat(row.avg_mad);
                            const numDays = parseFloat(row.num_days || 0) || 0;
                            const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
                            const totalUnitsSold = numDays * avgDailyUnits;
                            
                            if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
                              totalMAPEWeighted += mape * totalUnitsSold;
                              totalUnitsForMAPE += totalUnitsSold;
                            }
                            
                            if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
                              totalMADWeighted += mad * totalUnitsSold;
                              totalUnitsForMAD += totalUnitsSold;
                            }
                          });
                        });
                        
                        const avgMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
                        const avgMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
                        const overallStatus = calculateStatusFromMetrics(avgMAPE, avgMAD);
                        return overallStatus ? getStatusColor(overallStatus) : 'grey.50';
                      })(),
                    color: comparisonMetrics.hasData
                      ? (comparisonOverall.status ? getStatusTextColor(comparisonOverall.status) : 'inherit')
                      : (() => {
                        if (!transformedData.overallMetrics || Object.keys(transformedData.overallMetrics).length === 0) return 'inherit';
                        let totalMAPEWeighted = 0;
                        let totalMADWeighted = 0;
                        let totalUnitsForMAPE = 0;
                        let totalUnitsForMAD = 0;
                        
                        transformedData.dates.forEach(date => {
                          const dateRows = filteredData.filter(row => row.model_date === date);
                          dateRows.forEach(row => {
                            const mape = parseFloat(row.avg_mape);
                            const mad = parseFloat(row.avg_mad);
                            const numDays = parseFloat(row.num_days || 0) || 0;
                            const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
                            const totalUnitsSold = numDays * avgDailyUnits;
                            
                            if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
                              totalMAPEWeighted += mape * totalUnitsSold;
                              totalUnitsForMAPE += totalUnitsSold;
                            }
                            
                            if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
                              totalMADWeighted += mad * totalUnitsSold;
                              totalUnitsForMAD += totalUnitsSold;
                            }
                          });
                        });
                        
                        const avgMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
                        const avgMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
                        const overallStatus = calculateStatusFromMetrics(avgMAPE, avgMAD);
                        return overallStatus ? getStatusTextColor(overallStatus) : 'inherit';
                      })(),
                    width: 80,
                    minWidth: 80
                  }}
                >
                  {comparisonMetrics.hasData
                    ? (comparisonOverall.status || '-')
                    : (transformedData.overallMetrics && Object.keys(transformedData.overallMetrics).length > 0 ? (
                      (() => {
                        let totalMAPEWeighted = 0;
                        let totalMADWeighted = 0;
                        let totalUnitsForMAPE = 0;
                        let totalUnitsForMAD = 0;
                        
                        transformedData.dates.forEach(date => {
                          const dateRows = filteredData.filter(row => row.model_date === date);
                          dateRows.forEach(row => {
                            const mape = parseFloat(row.avg_mape);
                            const mad = parseFloat(row.avg_mad);
                            const numDays = parseFloat(row.num_days || 0) || 0;
                            const avgDailyUnits = parseFloat(row.avg_daily_units_sold || 0) || 0;
                            const totalUnitsSold = numDays * avgDailyUnits;
                            
                            if (!isNaN(mape) && mape >= 0 && totalUnitsSold > 0) {
                              totalMAPEWeighted += mape * totalUnitsSold;
                              totalUnitsForMAPE += totalUnitsSold;
                            }
                            
                            if (!isNaN(mad) && mad >= 0 && totalUnitsSold > 0) {
                              totalMADWeighted += mad * totalUnitsSold;
                              totalUnitsForMAD += totalUnitsSold;
                            }
                          });
                        });
                        
                        const avgMAPE = totalUnitsForMAPE > 0 ? totalMAPEWeighted / totalUnitsForMAPE : null;
                        const avgMAD = totalUnitsForMAD > 0 ? totalMADWeighted / totalUnitsForMAD : null;
                        const overallStatus = calculateStatusFromMetrics(avgMAPE, avgMAD);
                        
                        return overallStatus || '-';
                      })()
                    ) : '-')}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Showing {sortedData.length} of {transformedData.rows.length} items
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}

export default AccuracyTrends;
